import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import Image from 'next/future/image';
import BrandsFilter from '@/components/collections-v2/collection-nav-page/BrandsFilter';
import SortDropdown from '@/components/collections-v2/common/SortDropdown';
import filtersIcon from '@/public/icons/collection/filters.svg';
import useFilters from '../../../../lib/use-filters';
import { trackProductCollectionFiltered } from '@/lib/analytics/trackProduct';
import { Options } from '@/lib/collection-search-helpers';
import { sortList } from '@/components/collections-v2/common/constants';
import {
  searchToQuery,
  streamlineValue,
} from '@/components/collections-v2/collection-nav-page/helpers';
import SidebarFilters from '@/components/collections-v2/common/SidebarFilters';
import FiltersModal from '@/components/collections-v2/common/FiltersModal';

import { useCollectionSearchApi } from '@/lib/collections/use-collection-search-api';
import { CollectionProps, SortType } from '../types';
import InfiniteScroll from '../InfiniteScroll';

const CollectionPage = (props: CollectionProps) => {
  // Destructure Props
  const {
    offers,
    priceRange: _priceRange,
    options,
    products,
    searchInput,
    fixedFilters,
    categoryPk,
    conditionOptions,
  } = props;

  // Constants
  const priceRange = {
    min: Number(_priceRange?.min?.toFixed(2) ?? 0),
    max: Number(_priceRange?.max?.toFixed(2) ?? 0),
  };

  // State Management
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [allVisibleFiltersSelected, setAllVisibleFiltersSelected] = useState<{
    [key: string]: boolean;
  }>({});
  const [lastAllFiltersUnselected, setLastAllFiltersUnselected] = useState<{
    [key: string]: boolean;
  }>({});

  const [sortSelected, setSortSelected] = useState(
    sortList.find((sortItem) => sortItem.value === searchInput.sort) ??
      sortList[3],
  );

  // Filters
  const {
    numberOfFilters,
    shouldShowClearAll,
    searchParams,
    dispatchSearchInput,
    searchApiQs,
  } = useFilters({
    fixedFilters,
    searchInput,
  });

  // Data Fetching
  const { data, loading } = useCollectionSearchApi(searchApiQs, {
    offers,
    products,
    options,
    pagination: props.pagination,
  });

  // Loading
  const isLoading = useMemo(
    () => loading && !initialLoad,
    [loading, initialLoad],
  );

  // Initial setup of unchanged filters
  const initialUnchangedFilters: Options = {};

  // Initialize unchanged filters based on existing options
  Object.keys(searchInput.variantFilters)
    .filter((f) => options?.[f]?.length)
    .forEach((f) => {
      initialUnchangedFilters[f] = options?.[f];
    });

  const [unchangedFilters, setUnchangedFilters] = useState<Options>(
    initialUnchangedFilters,
  );

  // Page Filter Change Tracking
  useEffect(() => {
    if (!initialLoad) {
      const params = Object.fromEntries(
        new URLSearchParams(searchToQuery(searchParams)),
      );

      trackProductCollectionFiltered(offers, params);
    }
  }, [searchParams]);

  // Filters Methods
  const handleSortChange = (s: SortType) => {
    setInitialLoad(false);
    setSortSelected(s);
    dispatchSearchInput({
      type: 'sort',
      payload: s.value,
    });
  };

  const handleFilterChange = (
    variant: string,
    value: string,
    checked: boolean,
    inverse = false,
  ) => {
    setInitialLoad(false);
    const allVisbleUnselected =
      !checked &&
      (searchParams.variantFilters[variant] ?? []).length === 1 &&
      (searchParams.variantFilters[variant]?.includes(value) ?? false);

    setLastAllFiltersUnselected({ [variant]: allVisbleUnselected });

    // limiting scope to categories for now
    if (variant === 'categories') {
      const allVisbleSelected =
        checked &&
        (options?.[variant]?.length ?? 0) ===
          [value, ...(searchParams.variantFilters[variant] ?? [])].length &&
        (options?.[variant]?.every(
          (element) =>
            [value, ...(searchParams.variantFilters[variant] ?? [])].includes(
              element.key,
            ) ?? false,
        ) ??
          false);

      allVisibleFiltersSelected[variant] = allVisbleSelected;
      setAllVisibleFiltersSelected(allVisibleFiltersSelected);
    }

    dispatchSearchInput({
      type: variant,
      payload: {
        value: streamlineValue(value),
        selectedFilterValues: searchParams.variantFilters[variant],
        allFilterValues: fixedFilters.variantFilters[variant],
        visibleFilters: options?.[variant],
        inverse,
        checked,
      },
    });
  };

  const handlePageChange = (value: number) => {
    setInitialLoad(false);
    dispatchSearchInput({
      type: 'page',
      payload: {
        value,
      },
    });
  };

  useEffect(() => {
    // Loop through all the filters in the initial unfiltered options
    Object.keys(options ?? []).forEach((f) => {
      // if the filter has applied options, update the unchangedFilters accordingly
      if (searchParams.variantFilters[f]?.length) {
        setUnchangedFilters((prevFilters) => ({
          ...prevFilters,
          [f]: prevFilters[f] || data.options?.[f],
        }));
      } else {
        // If the filter does not have any options applied, remove it from unchangedFilters
        setUnchangedFilters((prevFilters) => {
          const _prevFilters = { ...prevFilters };

          delete _prevFilters[f];

          return _prevFilters;
        });
      }
    });
  }, [searchParams.variantFilters]);

  // Generate Filters Options
  const dynamicFilters = useMemo(
    () =>
      Object.keys(data.options ?? []).reduce((acc, key) => {
        const currentFilter = key;

        const filterOptions = data?.options?.[currentFilter];
        const unchangedFilter =
          unchangedFilters[currentFilter] ?? filterOptions;
        const isFilterApplied =
          !!searchParams.variantFilters?.[currentFilter]?.length;

        acc[currentFilter] = isFilterApplied ? unchangedFilter : filterOptions;

        return acc;
      }, {} as Options),
    [data.options, searchParams.variantFilters, unchangedFilters],
  );

  const clearAllFilters = () => {
    setInitialLoad(false);
    setAllVisibleFiltersSelected({});
    setLastAllFiltersUnselected({});
    dispatchSearchInput({
      type: 'reset',
      payload: fixedFilters,
    });
  };

  const handlePriceRangeChange = ({
    min,
    max,
  }: {
    min: number | undefined;
    max: number | undefined;
  }) => {
    setInitialLoad(false);
    dispatchSearchInput({
      type: 'minMaxPrice',
      payload: {
        minPrice: min,
        maxPrice: max,
      },
    });
  };

  return (
    <div className="mt-5 grid grid-cols-12 gap-x-5">
      {/* Left Section */}
      <section className="col-span-4 hidden h-fit rounded-md border lg:block xl:col-span-3">
        <SidebarFilters
          options={dynamicFilters}
          priceRange={priceRange}
          handleFilterChange={handleFilterChange}
          searchParams={searchParams}
          clearAllFilters={clearAllFilters}
          shouldShowClearAll={shouldShowClearAll}
          isLoading={isLoading}
          fixedVariantFilters={fixedFilters.variantFilters}
          handlePriceRangeChange={handlePriceRangeChange}
          allVisibleFiltersSelected={allVisibleFiltersSelected}
          lastAllFiltersUnselected={lastAllFiltersUnselected}
          categoryPk={categoryPk}
          conditionOptions={conditionOptions}
        />
      </section>
      {/* Right Section */}
      <section className="col-span-12 gap-x-5 lg:col-span-8 xl:col-span-9">
        {/* Top Filter */}
        <div className="grid grid-cols-12 gap-y-2.5 gap-x-5">
          {/* Brands Filter */}
          <div className="order-2 col-span-12 lg:order-1 lg:col-span-9">
            <BrandsFilter
              handleFilterChange={handleFilterChange}
              searchInput={searchParams}
              brands={dynamicFilters.brand}
              isLoading={isLoading}
            />
          </div>
          <div className="order-1 col-span-12 mr-[-2px] grid h-10 grid-cols-2 divide-x rounded-md border text-sm lg:order-2 lg:col-span-3 lg:grid-cols-1 lg:divide-x-0">
            {/* Filters Mobile CTA */}
            <button
              type="button"
              className={cn(
                'flex items-center justify-center gap-x-3 disabled:opacity-40 lg:hidden',
                {
                  'hover:underline hover:underline-offset-2': !isLoading,
                },
              )}
              onClick={() => setIsFilterOpen(true)}
              disabled={isLoading}
            >
              Filters {numberOfFilters > 0 && `(${numberOfFilters})`}
              <div className="h-4 w-4">
                <Image
                  src={filtersIcon}
                  height={50}
                  width={50}
                  alt="Filters"
                  className="h-full w-full"
                />
              </div>
            </button>
            {/* Sort Dropdown  */}
            <SortDropdown
              sortList={sortList}
              sortSelected={sortSelected}
              onSortChange={handleSortChange}
              isLoading={isLoading}
            />
          </div>
        </div>
        {/* Products List  */}
        <InfiniteScroll
          offers={data.offers}
          pagination={data.pagination}
          popularProductGroups={props.popularProductGroups}
          handlePageChange={handlePageChange}
          isLoading={isLoading}
          conditionOptions={conditionOptions}
        />
        {/* <ProductsList isLoading={isLoading} data={data} /> */}
      </section>
      {/* Filters Modal */}
      <FiltersModal
        options={dynamicFilters}
        priceRange={priceRange}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        handleFilterChange={handleFilterChange}
        searchParams={searchParams}
        clearAllFilters={clearAllFilters}
        shouldShowClearAll={shouldShowClearAll}
        isLoading={isLoading}
        fixedVariantFilters={fixedFilters.variantFilters}
        handlePriceRangeChange={handlePriceRangeChange}
        allVisibleFiltersSelected={allVisibleFiltersSelected}
        lastAllFiltersUnselected={lastAllFiltersUnselected}
        categoryPk={categoryPk}
        conditionOptions={conditionOptions}
      />
    </div>
  );
};

export default CollectionPage;
